import {
  type Context,
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useNavigate } from '@repo/poly-hooks/routing';
import { useCurrentOrganizationCore } from '@repo/runtime-kit/organization-core/hooks/use-current-organization-core';

interface HomePageContextType {
  defaultRoute: string;
  // Wrapper for Ember apps to run the redirect in a runloop to avoid double-render errors
  redirectWrapper?: (redirect: () => void) => void;
}

const HomePageContext: Context<HomePageContextType | null> =
  createContext<HomePageContextType | null>(null);

export function HomePageProvider({
  defaultRoute,
  redirectWrapper,
  children,
}: PropsWithChildren<HomePageContextType>) {
  const value = useMemo(() => ({ defaultRoute, redirectWrapper }), [defaultRoute, redirectWrapper]);

  return <HomePageContext.Provider value={value}>{children}</HomePageContext.Provider>;
}

/**
 * Sync changes with apps/qonto-js/app/services/home-page.ts
 */
export function useHomePage() {
  const context = useContext(HomePageContext);
  const organizationCore = useCurrentOrganizationCore();
  const navigate = useNavigate();
  const defaultRoute = context?.defaultRoute ?? '';
  const redirectWrapper = context?.redirectWrapper;
  const organizationSlug = organizationCore.slug;

  const visitDefaultPage = useCallback(
    (slug: string = organizationSlug) => {
      navigate(`/organizations/${slug}${defaultRoute}`);
    },
    [navigate, organizationSlug, defaultRoute]
  );

  const replaceWithDefaultPage = useCallback(
    (slug: string = organizationSlug) => {
      navigate(`/organizations/${slug}${defaultRoute}`, { replace: true });
    },
    [navigate, organizationSlug, defaultRoute]
  );

  const defaultRouteAbsolutePath = `/organizations/${organizationSlug}${defaultRoute}`;

  const homePage = useMemo(
    () => ({
      visitDefaultPage,
      replaceWithDefaultPage,
      defaultRouteAbsolutePath,
      redirectWrapper,
    }),
    [visitDefaultPage, replaceWithDefaultPage, defaultRouteAbsolutePath, redirectWrapper]
  );

  if (!context) {
    throw new Error('useHomePage must be used within HomePageProvider');
  }

  return homePage;
}
